(() => {
  const MODEL_LABEL_OVERRIDES = {
    'seamaster-configurator': [
      { name: 'Case', options: ['Silver', 'Black'] },
      { name: 'Bezel', options: ['Blue', 'Black and gold', 'Black and silver'] },
      { name: 'Dial', options: ['Blue wave', 'Black and gold', 'Black and silver'] },
      { name: 'Hands', options: ['Gold', 'Silver'] },
      { name: 'Second Hand', options: ['Gold', 'Silver'] },
      { name: 'Bracelet', options: ['Steel bracelet', 'Mesh bracelet', 'Black rubber'] },
    ],
    'skx-configurator': [
      { name: 'Case', options: ['Silver', 'Black'] },
      { name: 'Bezel', options: ['Silver', 'Black and silver', 'Black'] },
      { name: 'Dial', options: ['Rainbow', 'Black', 'Green', 'Black sunburst'] },
      { name: 'Hands', options: ['Mercedes silver', 'Arrow silver'] },
      { name: 'Second Hand', options: ['Yellow', 'Black'] },
      { name: 'Strap', options: ['Black rubber', 'White rubber', 'Jubilee bracelet'] },
    ],
  };
  const NO_MAGNIFIER_ANSWER_ID = 'REGLAGE-DATE-WITHOUT-MAGNIFIER';
  const MAGNIFIER_OPTION_MODELS = new Set(['new-datejust-2', 'new-master-2']);
  const NO_MAGNIFIER_ONLY_DIALS = new Set(['Arabic Special Black', 'Who Cares ?', 'Ace']);
  const GMT_NO_DATE_DIALS = new Set([
    'Skeleton Silver',
    'Skeleton Black',
    'Arabic Black',
    'Arabic Green',
    'Arabic Ice Blue',
  ]);

  const moneyFormatter = (currency, locale) =>
    new Intl.NumberFormat(locale || 'en', { style: 'currency', currency: currency || 'USD' });
  const escapeHTML = (value) => String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
  const componentSlug = (value) => String(value || 'component')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const isChrono = (value) => /\bchrono\b/i.test(String(value || ''));
  const isMagnifierGroup = (value) => /^(date|magnifying glass)$/i.test(String(value || '').trim());

  function initializeConfigurator(root) {
    const entryParams = new URLSearchParams(window.location.search);
    if (entryParams.get('entry') === 'general' && !entryParams.has('model')) return;
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    const config = JSON.parse(root.querySelector('[data-reglage-configurator-data]').textContent);
    const models = new Map(config.models.map((model) => [model.slug, model]));
    const formatter = moneyFormatter(config.currency, config.locale);
    let model = models.values().next().value;
    let manifest = null;
    let groups = [];
    let availableGroups = [];
    let activeGroupIndex = 0;
    let selections = new Map();
    let isAddingToCart = false;
    let renderSequence = 0;

    const all = (selector) => [...root.querySelectorAll(selector)];
    const one = (selector) => root.querySelector(selector);
    const setText = (selector, value) => all(selector).forEach((node) => { node.textContent = value; });
    const layerUrl = (layer) =>
      config.themeAssetUrls?.[layer?.themeAsset] || layer?.url || layer?.imageUrl || '';
    const selectedLayer = (group) =>
      group.options.find((option) => option.answerId === selections.get(group.questionId)) ||
      group.options[0];

    function groupLayers(layers) {
      const grouped = new Map();
      layers.forEach((layer) => {
        if (!grouped.has(layer.partIndex)) grouped.set(layer.partIndex, []);
        grouped.get(layer.partIndex).push(layer);
      });
      const result = [...grouped.entries()]
        .sort((left, right) => left[0] - right[0])
        .map(([partIndex, options]) => ({
          partIndex,
          questionId: options[0].questionId,
          name: options[0].questionName || `Component ${partIndex + 1}`,
          options,
        }));

      const overrides = MODEL_LABEL_OVERRIDES[model.slug];
      if (overrides) {
        result.forEach((group) => {
          const override = overrides[group.partIndex];
          if (!override) return;
          group.name = override.name;
          group.options = group.options.map((option, index) => ({
            ...option,
            questionName: override.name,
            answerName: override.options[index] || `Option ${index + 1}`,
          }));
        });
      }

      if (MAGNIFIER_OPTION_MODELS.has(model.slug)) {
        const magnifierGroup = result.find((group) => isMagnifierGroup(group.name));
        if (
          magnifierGroup &&
          !magnifierGroup.options.some((option) => option.answerId === NO_MAGNIFIER_ANSWER_ID)
        ) {
          magnifierGroup.options.push({
            partIndex: magnifierGroup.partIndex,
            questionId: magnifierGroup.questionId,
            questionName: magnifierGroup.name,
            answerId: NO_MAGNIFIER_ANSWER_ID,
            answerName: 'Without magnifier',
            isEmptyLayer: true,
          });
        }
      }

      return result;
    }

    function deriveAvailableGroups() {
      let nextGroups = groups.map((group) => ({ ...group, options: [...group.options] }));

      if (model.slug === 'royal-oak-configurator') {
        const caseGroup = groups.find((group) => /^case$/i.test(group.name.trim()));
        const chronoCase = isChrono(selectedLayer(caseGroup)?.answerName);
        nextGroups = nextGroups
          .filter((group) => chronoCase || !/^chrono hand$/i.test(group.name.trim()))
          .map((group) => {
            if (!/^dial$/i.test(group.name.trim())) return group;
            return {
              ...group,
              options: group.options.filter((option) => isChrono(option.answerName) === chronoCase),
            };
          });
      }

      if (model.slug === 'new-datejust-2') {
        const dialGroup = groups.find((group) => /^dial$/i.test(group.name.trim()));
        const selectedDial = selectedLayer(dialGroup)?.answerName;
        if (NO_MAGNIFIER_ONLY_DIALS.has(selectedDial)) {
          nextGroups = nextGroups.map((group) => {
            if (!/^date$/i.test(group.name.trim())) return group;
            return {
              ...group,
              options: group.options.filter((option) => option.answerId === NO_MAGNIFIER_ANSWER_ID),
            };
          });
        }
      }

      if (model.slug === 'new-master-2') {
        const dialGroup = groups.find((group) => /^dial$/i.test(group.name.trim()));
        const selectedDial = selectedLayer(dialGroup)?.answerName;
        if (GMT_NO_DATE_DIALS.has(selectedDial)) {
          nextGroups = nextGroups.filter((group) => !isMagnifierGroup(group.name));
        }
      }

      return nextGroups.filter((group) => group.options.length > 0);
    }

    function reconcileAvailableGroups() {
      availableGroups = deriveAvailableGroups();
      availableGroups.forEach((group) => {
        if (!group.options.some((option) => option.answerId === selections.get(group.questionId))) {
          selections.set(group.questionId, group.options[0].answerId);
        }
      });
      availableGroups = deriveAvailableGroups();
      activeGroupIndex = Math.max(0, Math.min(availableGroups.length - 1, activeGroupIndex));
      return availableGroups;
    }

    function setDefaults() {
      selections = new Map();
      groups.forEach((group) => {
        const configured = manifest.startingPoint?.configuration?.[group.questionId];
        const answer = group.options.some((option) => option.answerId === configured)
          ? configured
          : group.options[0].answerId;
        selections.set(group.questionId, answer);
      });
    }

    async function renderStage() {
      const stage = one('[data-reglage-stage]');
      if (manifest.renderMode === 'reference-composite') {
        const sequence = ++renderSequence;
        const canvas = await window.ReglageReferenceCompositor.render(
          manifest, selections, config.themeAssetUrls
        );
        if (sequence === renderSequence) stage.replaceChildren(canvas);
        setText('[data-reglage-price]', formatter.format(model.price));
        return;
      }
      stage.innerHTML = availableGroups
        .map((group) => {
          const layer = selectedLayer(group);
          const src = layerUrl(layer);
          if (!src || layer.isEmptyLayer) return '';
          return `<img crossorigin="anonymous" src="${escapeHTML(src)}" alt="" style="z-index:${group.partIndex + 1}">`;
        })
        .join('');
      await Promise.all(
        [...stage.querySelectorAll('img')].map((image) => image.decode?.().catch(() => undefined))
      );
      setText('[data-reglage-price]', formatter.format(model.price));
    }

    function renderTabs() {
      const tabs = one('[data-reglage-tabs]');
      tabs.innerHTML = availableGroups
        .map(
          (group, index) =>
            `<button type="button" class="${index === activeGroupIndex ? 'is-active' : ''}" data-group="${index}">${escapeHTML(group.name)}</button>`
        )
        .join('');
      tabs.querySelectorAll('[data-group]').forEach((button) => {
        button.addEventListener('click', () => setActiveGroup(Number(button.dataset.group)));
      });
    }

    function renderOptions() {
      const group = availableGroups[activeGroupIndex];
      if (!group) return;
      const options = one('[data-reglage-options]');
      const previousQuestion = options.dataset.question;
      const previousScrollLeft = options.scrollLeft;
      const previousScrollTop = options.scrollTop;
      setText('[data-reglage-option-heading]', group.name);
      setText(
        '[data-reglage-option-count]',
        `${group.options.length} ${group.options.length === 1 ? 'option' : 'options'}`
      );
      options.dataset.component = componentSlug(group.name);
      options.dataset.referenceComposite = manifest.renderMode === 'reference-composite' ? 'true' : 'false';
      options.dataset.question = group.questionId;
      options.innerHTML = group.options
        .map((option) => {
          const active = option.answerId === selections.get(group.questionId);
          const src = layerUrl(option);
          const previewSrc = option.previewUrl || src;
          const preview = previewSrc
            ? `<img src="${escapeHTML(previewSrc)}" alt="" loading="lazy">`
            : '<span class="reglage-configurator__empty-preview" aria-hidden="true"><i></i></span>';
          return `<button type="button" class="${active ? 'is-active' : ''}${option.isEmptyLayer ? ' is-empty-layer' : ''}" data-answer="${escapeHTML(option.answerId)}" aria-pressed="${active}">
            <span class="reglage-configurator__option-media">${preview}</span>
            <strong>${escapeHTML(option.answerName || 'Option')}</strong>
          </button>`;
        })
        .join('');
      if (previousQuestion === group.questionId) {
        options.scrollLeft = previousScrollLeft;
        options.scrollTop = previousScrollTop;
      } else {
        options.scrollLeft = 0;
        options.scrollTop = 0;
      }
      options.querySelectorAll('[data-answer]').forEach((button) => {
        button.addEventListener('click', async () => {
          selections.set(group.questionId, button.dataset.answer);
          one('[data-reglage-message]').textContent = '';
          renderControls();
          await renderStage();
        });
      });
    }

    function renderSummary() {
      const rows = availableGroups
        .map(
          (group) =>
            `<div><span>${escapeHTML(group.name)}</span><strong>${escapeHTML(selectedLayer(group).answerName || 'Included')}</strong></div>`
        )
        .join('');
      one('[data-reglage-summary]').innerHTML = rows;
      one('[data-reglage-sheet-content]').innerHTML = rows;
    }

    function renderStep() {
      const group = availableGroups[activeGroupIndex];
      const step = activeGroupIndex + 1;
      const isLastStep = activeGroupIndex === availableGroups.length - 1;
      setText('[data-reglage-progress]', `${step} / ${availableGroups.length}`);
      setText('[data-reglage-step-kicker]', `Step ${step} of ${availableGroups.length}`);
      setText('[data-reglage-step-name]', group.name);
      setText('[data-reglage-selected]', selectedLayer(group).answerName || 'Included');
      one('[data-reglage-prev]').disabled = false;
      setText('[data-reglage-next-label]', isLastStep ? 'Add to cart' : 'Next');
      one('[data-reglage-next]').classList.toggle('is-cart-action', isLastStep);
      one('[data-reglage-next]').setAttribute(
        'aria-label',
        isLastStep ? 'Add configured watch to cart' : `Continue to ${availableGroups[activeGroupIndex + 1]?.name || 'next component'}`
      );
    }

    function renderControls() {
      reconcileAvailableGroups();
      renderTabs();
      renderOptions();
      renderSummary();
      renderStep();
    }

    function setActiveGroup(index) {
      reconcileAvailableGroups();
      activeGroupIndex = Math.max(0, Math.min(availableGroups.length - 1, index));
      renderControls();
    }

    async function capturePreview() {
      const composite = one('[data-reglage-stage] canvas');
      const images = [...one('[data-reglage-stage]').querySelectorAll('img')];
      await Promise.all(images.map((image) => image.decode?.().catch(() => undefined)));
      const canvas = document.createElement('canvas');
      canvas.width = 720;
      canvas.height = 720;
      const context = canvas.getContext('2d', { alpha: true });
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      if (composite) context.drawImage(composite, 0, 0, canvas.width, canvas.height);
      else images.forEach((image) => context.drawImage(image, 0, 0, canvas.width, canvas.height));
      return canvas.toDataURL('image/webp', 0.9);
    }

    function configurationProperties(configurationId) {
      reconcileAvailableGroups();
      const activeSelections = Object.fromEntries(
        availableGroups.map((group) => [group.questionId, selections.get(group.questionId)])
      );
      const properties = {
        Architecture: model.name,
        _reglage_configuration_id: configurationId,
        _reglage_configuration: JSON.stringify({
          architecture: model.slug,
          selections: activeSelections,
        }),
      };
      availableGroups.forEach((group) => {
        properties[group.name] = selectedLayer(group).answerName || 'Included';
        if (manifest.renderMode !== 'reference-composite') {
          const src = layerUrl(selectedLayer(group));
          if (src) properties[`_reglage_layer_${String(group.partIndex).padStart(3, '0')}`] = src;
        }
      });
      return properties;
    }

    async function storeConfigurationPreview(configurationId) {
      reconcileAvailableGroups();
      const layers = manifest.renderMode === 'reference-composite' ? [] : availableGroups
        .map((group) => ({
          src: layerUrl(selectedLayer(group)),
          zIndex: group.partIndex + 1,
        }))
        .filter((layer) => layer.src);

      try {
        localStorage.setItem(`reglage-layers:${configurationId}`, JSON.stringify(layers));
      } catch (error) {
        console.info('Reglage layer preview storage is unavailable.', error);
      }

      try {
        const preview = await capturePreview();
        localStorage.setItem(`reglage-preview:${configurationId}`, preview);
      } catch (error) {
        console.info('Reglage composite preview capture is unavailable.', error);
      }
    }

    async function fetchCartSections(sectionIds) {
      const rootUrl = window.Shopify?.routes?.root || '/';
      const sectionUrl = new URL(rootUrl, window.location.origin);
      sectionUrl.searchParams.set('sections', sectionIds.join(','));
      const response = await fetch(sectionUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('The cart could not be refreshed.');
      return response.json();
    }

    async function addToCart() {
      if (isAddingToCart) return;
      const buttons = [...all('[data-reglage-add]'), one('[data-reglage-next]')].filter(Boolean);
      const message = one('[data-reglage-message]');
      if (!model.variantId) {
        message.textContent = 'This architecture needs a Shopify product assigned in the theme editor.';
        return;
      }
      isAddingToCart = true;
      message.textContent = 'Preparing your configuration…';
      buttons.forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      });
      setText('[data-reglage-next-label]', 'Adding…');

      try {
        const configurationId = `${model.slug}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        await storeConfigurationPreview(configurationId);
        const cartDrawer = document.querySelector('cart-drawer');
        const sections = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
          ? cartDrawer.getSectionsToRender().map((section) => section.id)
          : [];
        const rootUrl = window.Shopify?.routes?.root || '/';
        const cartAddUrl = window.routes?.cart_add_url || `${rootUrl}cart/add.js`;
        const formData = new FormData();
        formData.append('id', String(model.variantId));
        formData.append('quantity', '1');
        Object.entries(configurationProperties(configurationId)).forEach(([key, value]) => {
          formData.append(`properties[${key}]`, value);
        });
        if (sections.length) {
          formData.append('sections', sections.join(','));
          formData.append('sections_url', window.location.pathname);
        }
        cartDrawer?.setActiveElement?.(document.activeElement);

        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'same-origin',
          body: formData,
        });
        const state = await response.json();
        if (!response.ok || state.status) {
          throw new Error(state.description || state.message || 'The configuration could not be added.');
        }

        if (sections.length && (!state.sections || sections.some((id) => !state.sections[id]))) {
          state.sections = await fetchCartSections(sections);
        }

        message.textContent = 'Your configured watch was added to the cart.';
        if (cartDrawer && state.sections) {
          cartDrawer.classList.remove('is-empty');
          cartDrawer.querySelector('.drawer__inner')?.classList.remove('is-empty');
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'reglage-configurator',
              productVariantId: String(model.variantId),
              cartData: state,
            });
          }
          cartDrawer.renderContents(state);
          setTimeout(() => window.ReglageTheme?.hydrateConfiguredCartPreviews(cartDrawer), 60);
        } else {
          window.location.assign(window.routes?.cart_url || `${rootUrl}cart`);
        }
      } catch (error) {
        message.textContent = `${error.message} Please try again.`;
      } finally {
        isAddingToCart = false;
        buttons.forEach((button) => {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        });
        renderStep();
      }
    }

    async function loadModel(slug) {
      model = models.get(slug) || models.values().next().value;
      setText('[data-reglage-price]', formatter.format(model.price));
      one('[data-reglage-message]').textContent = '';
      one('[data-reglage-stage]').innerHTML = '<span>Loading composition…</span>';
      const response = await fetch(model.manifestUrl);
      if (!response.ok) throw new Error(`Could not load ${model.name}`);
      manifest = await response.json();
      if (manifest.renderMode === 'reference-composite') {
        await window.ReglageReferenceCompositor.preparePreviews(manifest, config.themeAssetUrls);
      }
      groups = groupLayers(manifest.layers);
      activeGroupIndex = 0;
      setDefaults();
      reconcileAvailableGroups();
      setText('[data-reglage-model-name]', model.name);
      setText(
        '[data-reglage-layer-count]',
        manifest.renderMode === 'reference-composite'
          ? `${groups.length} components · ${groups.find((group) => group.name === 'Dial')?.options.length || 0} reference dials`
          : `${manifest.layers.length} high-resolution source layers`
      );
      one('[data-reglage-model-select]').value = model.slug;
      const url = new URL(window.location.href);
      url.searchParams.set('model', model.slug);
      history.replaceState({}, '', url);
      renderControls();
      await renderStage();
    }

    const select = one('[data-reglage-model-select]');
    select.innerHTML = config.models
      .map((item) => `<option value="${escapeHTML(item.slug)}">${escapeHTML(item.name)}</option>`)
      .join('');
    select.addEventListener('change', () => loadModel(select.value));
    all('[data-reglage-add]').forEach((button) => button.addEventListener('click', addToCart));
    all('[data-reglage-reset]').forEach((button) =>
      button.addEventListener('click', async () => {
        setDefaults();
        one('[data-reglage-message]').textContent = '';
        renderControls();
        await renderStage();
      })
    );
    one('[data-reglage-prev]').addEventListener('click', () => {
      if (activeGroupIndex === 0) window.history.back();
      else setActiveGroup(activeGroupIndex - 1);
    });
    one('[data-reglage-next]').addEventListener('click', () => {
      if (activeGroupIndex === availableGroups.length - 1) {
        addToCart();
      } else setActiveGroup(activeGroupIndex + 1);
    });
    one('[data-reglage-summary-open]').addEventListener('click', () => {
      const sheet = one('[data-reglage-sheet]');
      sheet.hidden = false;
      requestAnimationFrame(() => sheet.classList.add('is-open'));
    });
    all('[data-reglage-summary-close]').forEach((button) =>
      button.addEventListener('click', () => {
        const sheet = one('[data-reglage-sheet]');
        sheet.classList.remove('is-open');
        setTimeout(() => { sheet.hidden = true; }, 240);
      })
    );

    const requested = new URLSearchParams(window.location.search).get('model');
    loadModel(requested).catch((error) => {
      one('[data-reglage-message]').textContent = error.message;
    });
  }

  function initialize() {
    document.querySelectorAll('[data-reglage-configurator]').forEach(initializeConfigurator);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
